import type {ReactElement} from "react";
import React, {useRef} from "react";
import {Observer} from "mobx-react";
import styled from "styled-components";
import {useAppStore} from "../../../../StateManager";
import {Permission, XyiconFeature} from "../../../../generated/api/base";
import ListIcon from "../../icons/list.svg?react";
import FrameIcon from "../../icons/frame.svg?react";
import {onWorkspaceViewClick} from "../../topbar/ViewTabsCommon";
import {DropdownButtonStyled} from "../../interaction/DropdownButtonV5";
import {baseDistance, ELLIPSIS, FLEXCENTER, fontWeight, radius} from "../../styles/styles";
import {colorPalette} from "../../styles/colorPalette";
import type {View} from "../../../../data/models/View";
import {TextInputWrapperStyled} from "../../details/datatypes/TextInputV5";
import {ReactUtils} from "../../../utils/ReactUtils";
import type {Portfolio} from "../../../../data/models/Portfolio";
import {Debouncer} from "../../../../utils/function/Debouncer";
import {TitleStyled, WidgetListStyled, WidgetRowStyled, WidgetStyled} from "./PortfolioList";
import type {IRecentViewWidgetData} from "./HomeDashboard";

type DashboardRecentlyUsedViewsData = {
	viewName: string;
	viewItemFeature: XyiconFeature;
	icon: ReactElement;
	viewID: string;
	portfolioID: string;
};

interface IRecentViewListProps {
	readonly widgetData: IRecentViewWidgetData;
}

export const RecentViewList = (props: IRecentViewListProps) => {
	const {widgetData} = props;
	const appState = useAppStore((state) => state.appState);
	const viewItemsRefArray = useRef<HTMLDivElement[]>([]);
	const debouncer = useRef<Debouncer>(new Debouncer(500));

	const goToView = (view: View, portfolio: Portfolio, e: React.MouseEvent) => {
		e.stopPropagation();

		// In case of a "double-click" (one click after another), we could end up resetting the spaces state and redirecting to Spaces instead of opening the last active view.
		// Debouncing prevents multiple calls conflicting each other (on a slow network) and causing unexpected behavior.
		debouncer.current.debounce(() => {
			onWorkspaceViewClick(view, true, undefined, {portfolioRefId: portfolio.refId});
		});
	};

	return (
		<Observer>
			{() => {
				const recentlyUsedViews = appState.user?.preferences.recentlyUsedViews;
				let recentlyUsedViewData: DashboardRecentlyUsedViewsData[] = [];
				let recentViews: {viewId: string; portfolioId: string}[] = [];
				const allViews = appState.actions.getAllViews();

				recentlyUsedViews?.forEach((view) => {
					const viewData = appState.actions.getViewById(view.viewID as string);
					const portfolioData = appState.actions.getPortfolioById(view.portfolioID);
					if (
						viewData &&
						portfolioData &&
						allViews.some((v) => v?.id === viewData?.id) &&
						!recentViews.find((recentView) => recentView.viewId === viewData.id && recentView.portfolioId === portfolioData.id) &&
						viewData.getPermission(appState.user?.id) >= Permission.View &&
						![XyiconFeature.Portfolio, XyiconFeature.XyiconCatalog, XyiconFeature.Space].includes(viewData.itemFeature)
					) {
						const Icon = viewData?.data.feature === XyiconFeature.SpaceEditor ? FrameIcon : ListIcon;
						recentlyUsedViewData.push({
							viewName: viewData?.name,
							viewItemFeature: viewData?.data.feature,
							icon: <Icon />,
							viewID: viewData.id,
							portfolioID: view.portfolioID,
						});
						recentViews.push({viewId: viewData.id, portfolioId: portfolioData.id});
					}
				});

				return (
					<WidgetStyled>
						<TitleStyled $color={widgetData.title.color}>{widgetData.title.text}</TitleStyled>
						<WidgetListStyled className={ReactUtils.cls({empty: recentlyUsedViewData.length === 0})}>
							{recentlyUsedViewData.length > 0 ? (
								recentlyUsedViewData.map((recentlyUsedView, index) => {
									const portfolio = appState.actions.getPortfolioById(recentlyUsedView.portfolioID);
									const view = appState.actions.getViewById(recentlyUsedView.viewID);
									const uniqueViewPortfolioId = `${recentlyUsedView.viewID}${recentlyUsedView.portfolioID}`;
									if (index < 25 && portfolio && view) {
										return (
											<RecentViewRowStyled
												key={uniqueViewPortfolioId}
												ref={(ref) => {
													viewItemsRefArray.current[index] = ref;
												}}
												onClick={(e) => goToView(view, portfolio, e)}
											>
												<div className="viewName">
													{recentlyUsedView.icon}
													<span title={recentlyUsedView.viewName}>{recentlyUsedView.viewName}</span>
												</div>
												<DashBoardListRightSide>
													<span title={portfolio.name}>{portfolio.name}</span>
												</DashBoardListRightSide>
											</RecentViewRowStyled>
										);
									}
								})
							) : (
								<> No views to display. Views that you open will be shown here.</>
							)}
						</WidgetListStyled>
					</WidgetStyled>
				);
			}}
		</Observer>
	);
};

const DashBoardListRightSide = styled.div`
	${FLEXCENTER};
	font-weight: ${fontWeight.bold};
	color: ${colorPalette.gray.c700Dark};
	max-width: 50%;
	gap: ${baseDistance.sm};

	span {
		${ELLIPSIS}
	}
`;

const RecentViewRowStyled = styled(WidgetRowStyled)`
	gap: 20%;

	.viewName,
	${TextInputWrapperStyled}, ${DashBoardListRightSide} {
		max-width: 40%;
	}

	div {
		display: flex;
		align-items: center;
		gap: ${baseDistance.sm};
		${ELLIPSIS}

		svg {
			visibility: visible;
			display: block;
			min-width: 24px;
		}
	}

	${DropdownButtonStyled} {
		svg {
			display: none;
			color: ${colorPalette.gray.c950};
		}
	}

	&:hover {
		${DropdownButtonStyled} {
			min-width: 24px;
			svg {
				display: block;

				&:hover {
					border: 1px solid ${colorPalette.gray.c950};
					border-radius: ${radius.sm};
				}
			}
		}
	}
`;
